import styled from '@emotion/styled'

import type { FC, PropsWithChildren } from 'react'
import { useMemo } from 'react'

import type { Document as RichTextDocument } from '@contentful/rich-text-types'
import { BLOCKS } from '@contentful/rich-text-types'

import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroRecipeBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import RichText, { ColoredTypography, List } from '@segment/matcha/recipes/RichText'

import { getFontSizeVariant, getRichTextFontSizeVariant } from 'views/Template/shared/utils'

import type { Media } from '@segment/api/types/templates/base'

import HeroImage from './HeroImage'
import { headingCharacterLimitBreakpoints, paragraphCharacterLimitBreakpoints } from './breakpoints'

export const CONTENT_DOWNLOAD_HERO_ID = `content-download-hero`

const BLANK_RICH_TEXT = {
  nodeType: BLOCKS.DOCUMENT,
  data: {},
  content: [
    {
      nodeType: `paragraph`,
      data: {},
      content: [
        {
          nodeType: `text`,
          value: ``,
          marks: [],
          data: {},
        },
      ],
    },
  ],
}

interface HeroProps {
  overline?: string
  heading?: string
  description: RichTextDocument
  image?: Media
}

const Hero: FC<PropsWithChildren<HeroProps>> = ({ overline, heading = ``, description, image, children }) => {
  const richTextDescription = description ? description : BLANK_RICH_TEXT
  const headingVariant = useMemo(() => getFontSizeVariant(heading, headingCharacterLimitBreakpoints), [heading])
  const paragraphVariant = useMemo(
    () => getRichTextFontSizeVariant(richTextDescription as RichTextDocument, paragraphCharacterLimitBreakpoints),
    [richTextDescription]
  )

  const options: RichTextRenderOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH](_node, children) {
        return (
          <StyledColoredTypography variant={paragraphVariant} tag="p">
            {children}
          </StyledColoredTypography>
        )
      },
      [BLOCKS.UL_LIST]: (_node, children) => <StyledList>{children}</StyledList>,
    },
  }
  return (
    <Wrapper id={CONTENT_DOWNLOAD_HERO_ID}>
      <StyledHeroRecipeBase variant="2Col">
        <HeroRecipe.Copy>
          <CopyTextBase>
            <CopyText.Overline>{overline}</CopyText.Overline>
            <CopyText.Heading tag="h1" variant={headingVariant}>
              {heading}
            </CopyText.Heading>
          </CopyTextBase>
          <HeroRecipe.Form>{children}</HeroRecipe.Form>
        </HeroRecipe.Copy>
        {image && (
          <StyledHeroRecipeContents>
            <HeroImage src={image.src} alt={image.title} height={image.height} width={image.width} />
            <StyledRichText data={description} options={options} />
          </StyledHeroRecipeContents>
        )}
      </StyledHeroRecipeBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background: ${({ theme }) => theme.palette.twilio.green[10]};
`

const StyledHeroRecipeBase = styled(HeroRecipeBase)`
  ${HeroRecipe.Wrapper} {
    align-items: flex-start;
  }
`

const StyledRichText = styled(RichText)`
  margin-top: 24px;
`

const StyledHeroRecipeContents = styled(HeroRecipe.Contents)`
  justify-items: left;
`

const StyledColoredTypography = styled(ColoredTypography)`
  white-space: pre-line;
`

const StyledList = styled(List)`
  text-align: left;
`

export default Hero
